// ============================================================================
// LES MENUS DE TAUX DE TVA — les taux que le magasin propose, et celui qu'un article créé reçoit.
//
// UTILITÉ : la fiche (prix et marge), la création simple, l'assistant de gamme et l'onglet des taux proposent un taux. Ils
// lisent ici la même liste — celle du magasin (Référentiels › Taux de TVA), dans l'ordre de la loi — au lieu d'en recopier
// une chacun ; et une création pré-remplit le taux de sa famille, sinon celui du magasin, par la règle du contrat.
// ⚠️ LE TAUX QU'UNE FICHE PORTE RESTE DANS SON MENU, même retiré des taux proposés : sinon le menu montrerait un autre taux
//    que celui enregistré, et l'enregistrement suivant l'écraserait sans que personne l'ait choisi.
// ============================================================================
import { useEffect, useState } from 'react';
import { LEGAL_VAT_RATES_BP, articleDefaultVatRateBp, normalizeCode } from '@orgazone/sync-contract';
import { formatRateBp } from '@orgazone/core-vat';
import { api, type VatDefaults } from './api';

/** Une option de menu : le taux en points de base, en texte, et son affichage (« 5,5 % »). */
export interface VatRateOption { value: string; label: string }

/**
 * Les options d'un menu de taux — les taux proposés (tous ceux de la loi quand le réglage est illisible ou pas encore lu),
 * plus celui que la fiche porte déjà ; ceux de la loi dans son ordre, puis un taux ancien.
 */
export function vatRateOptions(offered: readonly number[] | null, current?: number | null): VatRateOption[] {
  const shown = new Set<number>(offered ?? LEGAL_VAT_RATES_BP);
  if (current != null) shown.add(current);
  const legal = LEGAL_VAT_RATES_BP.filter((bp) => shown.has(bp));
  const others = [...shown].filter((bp) => !LEGAL_VAT_RATES_BP.includes(bp)).sort((x, y) => y - x);
  return [...legal, ...others].map((bp) => ({ value: String(bp), label: formatRateBp(bp) }));
}

/**
 * Le taux d'un article créé dans cette famille — celui qu'elle porte, sinon celui du magasin (`articleDefaultVatRateBp`).
 * ⚠️ LA FAMILLE PAR SON LIBELLÉ NORMALISÉ, comme le producteur la retrouve à l'écriture. `null` : rien de lisible, l'écran
 *    laisse choisir.
 */
export function creationVatRateBp(defaults: VatDefaults, family: string): number | null {
  const norm = normalizeCode(family);
  const own = norm === '' ? undefined : defaults.families.find((f) => normalizeCode(f.code) === norm);
  return articleDefaultVatRateBp(own?.vatRateBp ?? null, defaults.storeDefaultBp);
}

/**
 * Ce qu'une création lit pour proposer un taux — `null` tant qu'on ne sait pas.
 * ⚠️ RELU CHAQUE FOIS QUE `active` REDEVIENT VRAI : une modale vit toute la session, et le magasin actif comme ses réglages
 *    peuvent changer entre deux ouvertures.
 * ⚠️ UN ÉCHEC LAISSE `null` : le menu propose alors tous les taux de la loi et rien n'est pré-rempli — un taux deviné serait
 *    enregistré par un clic distrait.
 */
export function useVatDefaults(active = true): VatDefaults | null {
  const [defaults, setDefaults] = useState<VatDefaults | null>(null);
  useEffect(() => {
    if (!active) return undefined;
    let live = true;
    setDefaults(null);
    api.vatDefaults().then((d) => { if (live) setDefaults(d); }).catch(() => { if (live) setDefaults(null); });
    return () => { live = false; };
  }, [active]);
  return defaults;
}
